"use client";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { useTranslations } from "next-intl";
import {
  cloneElement,
  Fragment,
  isValidElement,
  useState,
  type PropsWithChildren,
  type ReactElement,
} from "react";
import { Sheet } from "react-modal-sheet";

export function BusinessListMobileSort<T extends string>({
  options,
  value,
  onChange,
  disabled,
  children,
}: PropsWithChildren<{
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
}>) {
  const t = useTranslations();
  const [open, setOpen] = useState(false);

  const trigger = isValidElement(children)
    ? cloneElement(children as ReactElement<{ onClick?: () => void }>, {
        onClick: () => setOpen(true),
      })
    : null;

  return (
    <>
      {trigger}

      <Sheet isOpen={open} onClose={() => setOpen(false)}>
        <Sheet.Backdrop />
        <Sheet.Container className="rounded-t-2xl border-x-0 border-b-0">
          <Sheet.Header />

          <div className="border-b border-border px-4 pb-3 pt-2">
            <div className="flex items-center justify-between gap-3">
              <span className="w-9" />

              <h2 className="truncate text-base font-semibold text-foreground">
                {t("saas.search.sort.title")}
              </h2>

              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border"
                aria-label="Close sort"
              >
                <Icon icon="XClose" className="h-4 w-4" />
              </button>
            </div>
          </div>

          <Sheet.Content disableDrag>
            <div className="flex flex-col">
              <div className="overflow-y-auto pb-24">
                <Separator />

                <RadioGroup
                  onValueChange={(val) => {
                    onChange(val as T);
                    setOpen(false);
                  }}
                  value={value as string}
                  disabled={disabled}
                  className="gap-0"
                >
                  {options.map((opt, idx) => (
                    <Fragment key={opt}>
                      <Label
                        className="h-14 px-5 flex items-center justify-between gap-6 cursor-pointer"
                        htmlFor={opt}
                      >
                        <div className="text-[15px] font-medium text-foreground">
                          {t(`saas.search.sort.options.${opt}` as any)}
                        </div>

                        <RadioGroupItem
                          id={opt}
                          value={opt}
                          className="h-5 w-5 border-2 rounded-full"
                        />
                      </Label>

                      {idx < options.length - 1 && <Separator />}
                    </Fragment>
                  ))}
                </RadioGroup>

                <Separator />
              </div>

              <div className="border-t border-border bg-card px-4 py-3">
                <Button className="h-11 w-full" onClick={() => setOpen(false)}>
                  {t("common.done")}
                </Button>
              </div>
            </div>
          </Sheet.Content>
        </Sheet.Container>
      </Sheet>
    </>
  );
}
